/** @jsxImportSource @emotion/react */
import { Product, ProductAdditionGroup } from "../models/product";
import { Table } from "../../../components/table-components/Table";
import { TableRowHeader } from "../../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../../components/table-components/TableCellHeaderText";
import { firstTableCellStyle } from "../../../style-util";
import { TableBody } from "../../../components/table-components/TableBody";
import { TableRow } from "../../../components/table-components/TableRow";
import { TableCellText } from "../../../components/table-components/TableCellText";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { toastr } from "react-redux-toastr";
import { useUpdateProduct } from "../service/products-api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { TableEllipsisDropdown } from "../../../components/TableEllipsisDropdown";
import { DropdownItemLink } from "../../common/DropdownItemLink";
import { routes } from "../../../appRoutingHelpers";
import { getErrorMessage } from "../../../error-handler";

type Props = {
  additionGroups: ProductAdditionGroup[];
  product: Product;
};

export const AdditionGroupsTable = ({ additionGroups, product }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { mutateAsync: updateProduct, isLoading } = useUpdateProduct(
    product.id
  );

  return (
    <Table>
      <thead>
        <TableRowHeader>
          <TableCellHeaderText css={firstTableCellStyle}>
            Name
          </TableCellHeaderText>
          <TableCellHeaderText>Title</TableCellHeaderText>
          <th />
        </TableRowHeader>
      </thead>
      <TableBody>
        {additionGroups.map((g, i) => (
          <TableRow key={i}>
            <TableCellText css={firstTableCellStyle}>
              <StyledText
                className="bodyTextRegular mb-0"
                color={colors.textPrimary}
              >
                {g.name}
              </StyledText>
            </TableCellText>
            <TableCellText>
              <StyledText
                className="bodyTextRegular mb-0"
                color={colors.textPrimary}
              >
                {g.title}
              </StyledText>
            </TableCellText>
            <TableEllipsisDropdown
              menu={{
                items: [
                  {
                    key: "1",
                    label: (
                      <DropdownItemButton
                        onClick={async () => {
                          try {
                            await updateProduct({
                              organizationKeyName,
                              data: {
                                ...product,
                                labels: (product.labels || []).map((l) => l.id),
                                tags: (product.tags || []).map((t) => t.id),
                                productPackagingInstructions:
                                  product.packagingInstructions?.map((x) => ({
                                    id: x.id,
                                    amount: x.amount,
                                  })) || [],
                                additionGroups: (product.additionGroups || [])
                                  .filter((group) => group.id !== g.id)
                                  .map((g) => g.id),
                              },
                            });
                            toastr.success(
                              "Success",
                              "Addition group successfully removed"
                            );
                          } catch (error: unknown) {
                            const message = getErrorMessage(error);
                            toastr.error("Error", message);
                          }
                        }}
                        icon={<FontAwesomeIcon icon={solid("trash")} />}
                      >
                        {isLoading ? (
                          <ActionSpinner />
                        ) : (
                          "Remove addition group"
                        )}
                      </DropdownItemButton>
                    ),
                  },
                  {
                    key: "2",
                    label: (
                      <DropdownItemLink
                        to={routes.shopProductAdditionsEdit(
                          organizationKeyName,
                          g.id
                        )}
                        icon={
                          <FontAwesomeIcon
                            icon={solid("up-right-from-square")}
                          />
                        }
                      >
                        Edit addition group
                      </DropdownItemLink>
                    ),
                  },
                ],
              }}
            />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
